import { Controller } from "@hotwired/stimulus"
import "jsuites/dist/jsuites.js"
import jspreadsheet from "jspreadsheet-ce"

export default class extends Controller {
  static targets = ["container", "linesField", "totalDisplay"]

  static values = {
    services: Array,
    emptyRows: { type: Number, default: 7 }
  }

  connect() {
    this.idToService = new Map()
    const source = []

    for (const s of this.servicesValue || []) {
      if (!s || s.id == null || !s.label) continue
      const idKey = String(s.id)
      this.idToService.set(idKey, s)
      source.push({ id: idKey, name: s.label, value: idKey, text: s.label })
    }

    const data = Array.from({ length: this.emptyRowsValue }, () => ["", "", ""])

    this.jspreadsheet = jspreadsheet(this.containerTarget, {
      data,
      minSpareRows: 0,
      columns: [
        {
          type: "dropdown",
          title: "Prestación",
          width: 350,
          source,
          autocomplete: true
        },
        {
          type: "numeric",
          title: "Cantidad",
          width: 110
        },
        {
          type: "numeric",
          title: "Valor",
          width: 130,
          mask: "#.##,00",
          decimal: ",",
          align: "right"
        }
      ],
      allowInsertRow: true,
      allowDeleteRow: true,
      allowInsertColumn: false,
      allowDeleteColumn: false,
      columnInsert: false,
      tableOverflow: true,
      tableWidth: "100%",
      onafterchanges: (_instance, records) => {
        this.handleAfterChanges(records)
      },
      onload: () => {
        this.updateTotal()
      }
    })

    this.hideAboutRow()
    this.updateTotal()
  }

  disconnect() {
    if (this.jspreadsheet) {
      this.jspreadsheet.destroy()
      this.jspreadsheet = null
    }
  }

  handleAfterChanges(records) {
    if (!records || !this.jspreadsheet) return

    records.forEach((record) => {
      const columnIndex = this.extractColumn(record)
      const rowIndex = this.extractRow(record)
      if (!Number.isInteger(columnIndex) || !Number.isInteger(rowIndex)) return

      if (columnIndex === 0) {
        this.applyServiceDefaults(rowIndex)
      }
      if (columnIndex === 0 || columnIndex === 1) {
        this.enforceIntegerQuantity(rowIndex)
      }
    })

    this.updateTotal()
  }

  extractColumn(record) {
    if (Array.isArray(record)) return parseInt(record[1], 10)
    const v = record?.x ?? record?.col ?? record?.column
    return typeof v === "string" ? parseInt(v, 10) : v
  }

  extractRow(record) {
    if (Array.isArray(record)) return parseInt(record[2], 10)
    const v = record?.y ?? record?.row
    return typeof v === "string" ? parseInt(v, 10) : v
  }

  applyServiceDefaults(rowIndex) {
    const serviceId = this.jspreadsheet.getValueFromCoords(0, rowIndex)
    if (serviceId === "" || serviceId === null || serviceId === undefined) return

    const meta = this.idToService.get(String(serviceId))
    if (!meta) return

    const qtyRaw = this.parseDecimal(this.jspreadsheet.getValueFromCoords(1, rowIndex))
    const qtyInt = qtyRaw == null ? null : Math.max(0, Math.trunc(qtyRaw))
    if (qtyInt == null || qtyInt <= 0) {
      this.jspreadsheet.setValueFromCoords(1, rowIndex, 1, true)
    }

    if (meta.price == null) return

    const price = Number(meta.price)
    if (!Number.isFinite(price) || price < 0) return

    this.jspreadsheet.setValueFromCoords(2, rowIndex, price, true)
  }

  enforceIntegerQuantity(rowIndex) {
    const raw = this.jspreadsheet.getValueFromCoords(1, rowIndex)
    if (raw === "" || raw === null || raw === undefined) return

    const n = this.parseDecimal(raw)
    if (n == null) return

    const intVal = Math.max(0, Math.trunc(n))
    if (intVal !== n) {
      this.jspreadsheet.setValueFromCoords(1, rowIndex, intVal, true)
    }
  }

  integerQuantityAt(rowIndex) {
    const raw = this.jspreadsheet.getValueFromCoords(1, rowIndex)
    if (raw === "" || raw === null || raw === undefined) return 0
    const n = this.parseDecimal(raw)
    if (n == null) return 0
    return Math.max(0, Math.trunc(n))
  }

  parseDecimal(raw) {
    if (raw === "" || raw === null || raw === undefined) return null
    if (typeof raw === "number" && Number.isFinite(raw)) return raw

    const s = String(raw).trim().replace(/\s/g, "")
    if (s === "") return null

    if (/^\d+(,\d+)?$/.test(s)) return parseFloat(s.replace(",", "."))
    if (/^\d+(\.\d+)?$/.test(s)) return parseFloat(s)

    const normalized = s.replace(/\./g, "").replace(",", ".")
    const n = parseFloat(normalized)
    return Number.isFinite(n) ? n : null
  }

  updateTotal() {
    if (!this.jspreadsheet || !this.hasTotalDisplayTarget) return

    const rows = this.jspreadsheet.getData() || []
    let sum = 0

    for (let y = 0; y < rows.length; y++) {
      const qty = this.integerQuantityAt(y)
      const price = this.parseDecimal(this.jspreadsheet.getValueFromCoords(2, y)) || 0
      const serviceId = this.jspreadsheet.getValueFromCoords(0, y)
      if (serviceId === "" || serviceId === null || serviceId === undefined) continue
      if (!this.idToService.has(String(serviceId))) continue
      sum += qty * price
    }

    this.totalDisplayTarget.textContent = this.formatMoney(sum)
  }

  formatMoney(n) {
    try {
      return new Intl.NumberFormat("es-AR", {
        style: "currency",
        currency: "ARS",
        minimumFractionDigits: 2
      }).format(n)
    } catch {
      return n.toFixed(2)
    }
  }

  hideAboutRow() {
    if (this.jspreadsheet?.ads) {
      this.jspreadsheet.ads.classList.add("hidden")
    }
  }

  beforeSubmit(event) {
    event.preventDefault()
    if (!this.jspreadsheet) {
      this.element.requestSubmit()
      return
    }

    const rows = this.jspreadsheet.getData() || []
    const payload = []

    for (let y = 0; y < rows.length; y++) {
      const serviceId = this.jspreadsheet.getValueFromCoords(0, y)
      if (serviceId === "" || serviceId === null || serviceId === undefined) continue

      const meta = this.idToService.get(String(serviceId))
      if (!meta) continue

      const qty = this.integerQuantityAt(y)
      const price = this.parseDecimal(this.jspreadsheet.getValueFromCoords(2, y)) || 0

      payload.push({
        dental_service_id: meta.service_id || meta.id,
        quantity: qty > 0 ? qty : 1,
        unit_price: price >= 0 ? price : 0
      })
    }

    this.linesFieldTarget.value = JSON.stringify(payload)
    this.element.requestSubmit()
  }
}
